import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const PdfPage = () => {
  const [pdfs, setPdfs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    category: '',
    coverImage: null,
    file: null,
    active: true,
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);  

  useEffect(() => {
    fetchPdfs(currentPage, searchQuery);
    fetchCategories();
  }, [currentPage, searchQuery]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/admin/api/pdf-category');
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchPdfs = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(`/admin/api/pdfs?page=${page}&limit=${limit}&search=${search}`);
      const { pdfs = [], totalCount = 0 } = res.data;
      setPdfs(pdfs);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch PDFs:', err);
      toastError('Failed to fetch PDFs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      name: { required: true },
      category: { required: true },
      coverImage: { required: !editingId }, // Cover image required only for new PDFs
      file: { required: !editingId }, // PDF file required only for new PDFs
    });

    // Additional validation for file size and type
    if (formValues.coverImage instanceof File) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      
      if (formValues.coverImage.size > maxSize) {
        validationErrors.coverImage = 'Cover image size should be less than 5MB';
      }
      
      if (!allowedTypes.includes(formValues.coverImage.type)) {
        validationErrors.coverImage = 'Only JPEG, PNG, GIF, and WebP images are allowed for cover';
      }
    }

    if (formValues.file instanceof File) {
      const maxSize = 50 * 1024 * 1024; // 50MB for PDF files
      const allowedTypes = ['application/pdf'];
      
      if (formValues.file.size > maxSize) {
        validationErrors.file = 'PDF file size should be less than 50MB';
      }
      
      if (!allowedTypes.includes(formValues.file.type)) {
        validationErrors.file = 'Only PDF files are allowed';
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      
      formData.append('name', formValues.name || '');
      formData.append('category', formValues.category || '');
      formData.append('active', formValues.active ? 'true' : 'false');
      
      if (formValues.coverImage instanceof File) {
        formData.append('coverImage', formValues.coverImage);
      }
      
      if (formValues.file instanceof File) {
        formData.append('file', formValues.file);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (editingId) {
        await axios.put(`/admin/api/pdfs/${editingId}`, formData, config);
        toastSuccess('PDF updated successfully!');
      } else {
        await axios.post('/admin/api/pdfs', formData, config);
        toastSuccess('PDF added successfully!');
      }
      setCurrentPage(1);
      fetchPdfs(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save PDF:', err);
      toastError(err.response?.data?.message || 'Failed to save PDF');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (pdf) => {
    setFormValues({
      name: pdf.name || '',
      category: pdf.category || '',
      coverImage: null, // Don't preload the image file for editing
      file: null, // Don't preload the PDF file for editing
      active: pdf.active || false,
    });
    setCoverImagePreview(pdf.coverImage || '');
    setEditingId(pdf._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this PDF?')) {
      try {
        await axios.delete(`/admin/api/pdfs/${id}`);
        toastSuccess('PDF deleted successfully!');
        setCurrentPage(1);
        fetchPdfs(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete PDF:', err);
        toastError('Failed to delete PDF');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ 
      name: '',
      category: '',
      coverImage: null,
      file: null,
      active: true,
    });
    setCoverImagePreview('');
    setEditingId(null);
    setErrors({});
  };

  const handleCoverImageChange = (name, file) => {
    setFormValues({ ...formValues, [name]: file });
    if (file) {
      setCoverImagePreview(URL.createObjectURL(file));
    } else {
      setCoverImagePreview('');
    }
  };

  const handleFileChange = (name, file) => {
    setFormValues({ ...formValues, [name]: file });
  };

  const handleViewPdf = (pdf) => {
    setSelectedPdf(pdf);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append('active', !currentStatus ? 'true' : 'false');
      
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      await axios.put(`/admin/api/pdfs/${id}`, formData, config);
      toastSuccess(`PDF ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchPdfs(currentPage, searchQuery);
    } catch (err) {
      console.error('Failed to update PDF status:', err);
      toastError('Failed to update PDF status');
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> PDFs</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search PDFs..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">PDF List</h5>
          <button
            className="btn btn-sm btn-success"
            onClick={() => setShowModal(true)}
          >
            + Add New PDF
          </button>
        </div>

        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
            style={{ fontSize: '14px', lineHeight: '2' }}
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Name</th>
                <th className="py-1 px-2">Category</th>
                <th className="py-1 px-2">Cover Image</th>
                <th className="py-1 px-2">PDF File</th>
                <th className="py-1 px-2">Status</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : pdfs.length > 0 ? (
                pdfs.map((pdf, idx) => (
                  <tr key={pdf._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">
                      <span style={{ fontSize: '13px', fontWeight: '500' }}>
                        {pdf.name}
                      </span>
                    </td>
                    <td className="py-1 px-2">
                      <span style={{ fontSize: '12px' }}>
                        {pdf.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="py-1 px-2">
                      {pdf.coverImage ? (
                        <img 
                          src={pdf.coverImage} 
                          alt="Cover" 
                          style={{ 
                            width: '50px', 
                            height: '40px', 
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(pdf.coverImage, '_blank')}
                          title="Click to view cover image"
                        />
                      ) : (
                        <span className="text-muted">No Cover</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {pdf.file ? (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          style={{ fontSize: '11px', padding: '2px 8px' }}
                          onClick={() => window.open(pdf.file, '_blank')}
                          title="Open PDF"
                        >
                          View PDF
                        </button>
                      ) : (
                        <span className="text-muted">No File</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      <span 
                        className={`badge ${pdf.active ? 'badge-success' : 'badge-secondary'}`}
                        style={{
                          backgroundColor: pdf.active ? '#28a745' : '#6c757d',
                          color: 'white',
                          fontSize: '11px',
                          padding: '4px 8px',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleStatus(pdf._id, pdf.active)}
                        title={`Click to ${pdf.active ? 'deactivate' : 'activate'}`}
                      >
                        {pdf.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0"
                        style={{
          
                          color: '#C44C23',
                    
                        }}
                        title="Edit"
                        onClick={() => handleEdit(pdf)}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0"
                        style={{
          
                          color: 'red',
                    
                        }}
                        title="Delete"
                        onClick={() => handleDelete(pdf._id)}
                      >
                        <FaTrash size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-2">No PDFs found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className="btn btn-sm"
                  style={{
                    backgroundColor: currentPage === page ? '#C44C23' : 'transparent',
                    color: currentPage === page ? 'white' : '#C44C23',
                    border: '1px solid #C44C23'
                  }}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>

      <CommonModalForm
        visible={showModal}
        title={editingId ? 'Edit PDF' : 'Add PDF'}
        fields={[
          { 
            name: 'name', 
            label: 'Name', 
            type: 'text',
            placeholder: 'Enter PDF name'
          },
          { 
            name: 'category', 
            label: 'Category', 
            type: 'select',
            placeholder: 'Select category',
            options: categories.map(cat => ({ value: cat._id, label: cat.name }))
          },
          { 
            name: 'coverImage', 
            label: 'Cover Image Upload', 
            type: 'file',
            placeholder: 'Select cover image (JPEG, PNG)',
            accept: 'image/*'
          },
          { 
            name: 'file', 
            label: 'File Upload', 
            type: 'file',
            placeholder: 'Select PDF file',
            accept: '.pdf'
          },
          { 
            name: 'active', 
            label: 'Active Status', 
            type: 'checkbox',
            placeholder: 'Mark as active'
          },
        ]}
        values={formValues}
        errors={errors}
        onChange={(updatedValues) => {
          console.log('Form values updated:', updatedValues);
          if (updatedValues.coverImage instanceof File) {
            handleCoverImageChange('coverImage', updatedValues.coverImage);
          } else if (updatedValues.coverImage === null) {
            setFormValues({ ...formValues, coverImage: null });
            setCoverImagePreview('');
          } else if (updatedValues.file instanceof File) {
            handleFileChange('file', updatedValues.file);
          } else {
            setFormValues(updatedValues);
          }
        }}
        onSubmit={handleSave}
        onCancel={handleCancel}
        submitLabel={saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
        requiredFields={editingId ? ['name', 'category'] : ['name', 'category', 'coverImage', 'file']}
        imagePreview={coverImagePreview}
        disabled={saving}
      />

      {/* View PDF Modal */}
      {showViewModal && selectedPdf && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">PDF Preview</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowViewModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 text-center">
                    <h6>Cover Image</h6>
                    {selectedPdf.coverImage ? (
                      <img
                        src={selectedPdf.coverImage}
                        alt="PDF Cover"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '300px',
                          objectFit: 'contain',
                          border: '1px solid #ddd',
                          borderRadius: '8px'
                        }}
                      />
                    ) : (
                      <div 
                        style={{ 
                          height: '200px', 
                          border: '2px dashed #ccc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '8px'
                        }}
                      >
                        <span className="text-muted">No cover image</span>
                      </div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <h6>PDF Details</h6>
                    <div className="mb-3">
                      <p><strong>Name:</strong> {selectedPdf.name}</p>
                      <p><strong>Category:</strong> {selectedPdf.category || 'Uncategorized'}</p>
                      <p><strong>Status:</strong> 
                        <span className={`ms-2 badge ${selectedPdf.active ? 'badge-success' : 'badge-secondary'}`}>
                          {selectedPdf.active ? 'Active' : 'Inactive'}
                        </span>
                      </p>
                      <p><strong>Created:</strong> {new Date(selectedPdf.createdAt).toLocaleDateString()}</p>
                    </div>
                    {selectedPdf.file && (
                      <div className="mt-3">
                        <iframe
                          src={selectedPdf.file}
                          style={{
                            width: '100%',
                            height: '300px',
                            border: '1px solid #ddd',
                            borderRadius: '8px'
                          }}
                          title="PDF Preview"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                {selectedPdf.coverImage && (
                  <button
                    type="button"
                    className="btn btn-info"
                    onClick={() => window.open(selectedPdf.coverImage, '_blank')}
                  >
                    Open Cover Image
                  </button>
                )}
                {selectedPdf.file && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => window.open(selectedPdf.file, '_blank')}
                  >
                    Open PDF File
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfPage;
